/**
 * WI 32156 + WI 32207 — access + cost-visibility gates for the cycle-count UI.
 *
 * Two Software Security features drive cycle-count access:
 *  - `cyclecountadmin` — the admin capability. Holding it at ViewFeature level
 *    grants the Cycle Count Admin page, the material cost columns, and the
 *    variance report, on BOTH web and mobile (WI 32207 decision #2: the gate is
 *    ViewFeature, not EditAll — EditAll still separately gates the destructive
 *    server writes, delete-line + post-adjustments).
 *  - `cyclecountuser` — a mobile-only counter capability. Holding either it OR
 *    `cyclecountadmin` at ViewFeature level grants access to the count list +
 *    session (the counting surface).
 *
 * Cost hiding is UI-only: the API still returns the cost fields, so these
 * predicates govern display, not data access (accepted tradeoff, per WI 32156
 * and confirmed UI-only in WI 32207).
 *
 * Fail-closed: a missing, null, or non-view grant denies. The shape is a
 * minimal structural subset of the Software Security feature object returned by
 * `SecurityService.getFeatureById(...)`, so these stay pure, framework-free
 * predicates testable in the lib (the NativeScript app has no vitest runner).
 */
export interface CycleCountCostFeatureGrant {
  viewFeature?: boolean;
  editAll?: boolean;
  viewAll?: boolean;
}

/**
 * True when the caller holds the `cyclecountadmin` feature at ViewFeature level.
 * This is the single admin gate for the Cycle Count Admin page, cost columns,
 * and variance report (web + mobile). Fails closed on an absent grant.
 */
export function hasCycleCountAdminAccess(
  feature: CycleCountCostFeatureGrant | null | undefined
): boolean {
  return feature?.viewFeature === true;
}

/**
 * True when the caller may view material cost — same gate as admin access
 * (cyclecountadmin ViewFeature). Kept as a named predicate for the cost call
 * sites (web variance report + mobile session).
 */
export function canViewCycleCountCost(
  feature: CycleCountCostFeatureGrant | null | undefined
): boolean {
  return hasCycleCountAdminAccess(feature);
}

/**
 * True when the caller may access the cycle-count counting surface (count list +
 * session): holds `cyclecountuser` OR `cyclecountadmin` at ViewFeature level.
 * Fails closed when NEITHER grant confers view.
 */
export function canAccessCycleCount(
  userFeature: CycleCountCostFeatureGrant | null | undefined,
  adminFeature: CycleCountCostFeatureGrant | null | undefined
): boolean {
  return (
    userFeature?.viewFeature === true || adminFeature?.viewFeature === true
  );
}
